import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp, HelpCircle, CreditCard, DollarSign, Truck, Shield, User, Package } from 'lucide-react';

const FAQItem = ({ question, answer, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="stat-card overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-5 flex items-center justify-between text-left hover:bg-zinc-800/50 transition-colors"
        data-testid={`faq-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400 flex-shrink-0" />}
          <span className="font-medium text-white">{question}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-zinc-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-0">
          <div className="pl-8 text-zinc-400 text-sm leading-relaxed border-l-2 border-blue-500/30">
            {answer}
          </div>
        </div>
      )}
    </div>
  );
};

const QAPage = () => {
  const faqs = [
    {
      question: "Why is there a 3.5% fee on my purchase?",
      answer: (
        <div>
          <p>
            At Slab Society, we charge <strong className="text-emerald-400">0% commission</strong> on all sales. The 3.5% you see is a flat payment processing fee required by our secure payment partners (like Stripe) to ensure your transaction is protected. <strong className="text-white">We don't keep a penny of it.</strong>
          </p>
        </div>
      ),
      icon: CreditCard
    },
    {
      question: "How do I cancel my $20 membership?",
      answer: (
        <div>
          <p className="mb-3">
            You can cancel your seller membership anytime:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Go to your <strong className="text-white">Dashboard</strong></li>
            <li>Click on <strong className="text-white">Membership</strong> or <strong className="text-white">Billing</strong></li>
            <li>Select <strong className="text-white">"Cancel Subscription"</strong></li>
          </ol>
          <p className="mt-3 text-zinc-300">
            Your membership will remain active until the end of your current billing period. You won't be charged again after cancellation.
          </p>
          <p className="mt-3 text-zinc-500 text-xs">
            Note: Active listings will be deactivated when your membership ends. You can resubscribe anytime to reactivate them.
          </p>
        </div>
      ),
      icon: DollarSign
    },
    {
      question: "How does 0% seller commission work?",
      answer: (
        <div>
          <p className="mb-3">
            Unlike other marketplaces that take 10-15% of each sale, <strong className="text-white">Slab Society takes $0 from your sales</strong>.
          </p>
          <p className="mb-3">
            Instead, we charge a flat $20/month membership fee. This means:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Sell a $100 card → You keep $100</li>
            <li>Sell a $1,000 card → You keep $1,000</li>
            <li>Sell unlimited cards → Same $20/month</li>
          </ul>
          <p className="mt-3 text-emerald-400">
            The more you sell, the more you save compared to commission-based platforms.
          </p>
        </div>
      ),
      icon: DollarSign
    },
    {
      question: "How does shipping work?",
      answer: (
        <div>
          <p className="mb-3">
            We've integrated with <strong className="text-white">Shippo</strong> for easy shipping:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>When you sell a card, go to your <strong className="text-white">Seller Dashboard → Orders</strong></li>
            <li>Click <strong className="text-white">"Get Shipping Rates"</strong> to see live rates from USPS, UPS, and FedEx</li>
            <li>Select a rate and <strong className="text-white">"Buy Label"</strong></li>
            <li>Print your label and ship the card</li>
          </ol>
          <p className="mt-3 text-zinc-300">
            Tracking is automatically sent to the buyer via email.
          </p>
        </div>
      ),
      icon: Truck
    },
    {
      question: "Is my payment information secure?",
      answer: (
        <div>
          <p className="mb-3">
            <strong className="text-white">Yes, absolutely.</strong> We use Stripe, a PCI-compliant payment processor trusted by millions of businesses worldwide.
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Your card details never touch our servers</li>
            <li>All data is encrypted using bank-level security</li>
            <li>Stripe handles fraud detection automatically</li>
          </ul>
        </div>
      ),
      icon: Shield
    },
    {
      question: "How does the AI Card Scanner work?",
      answer: (
        <div>
          <p className="mb-3">
            Our AI Scanner uses <strong className="text-white">GPT-4 Vision</strong> to instantly identify your cards:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Upload a photo of your card</li>
            <li>AI identifies the player, year, set, and parallel type</li>
            <li>Get instant market valuation based on recent sales</li>
          </ol>
          <p className="mt-3 text-zinc-300">
            The scanner detects parallels like Silver Prizm, Refractors, numbered cards (/99, /25, etc.), and more.
          </p>
        </div>
      ),
      icon: Package
    },
    {
      question: "Do I need to be 18 to use Slab Society?",
      answer: (
        <div>
          <p className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400">
            <strong>Yes, you must be at least 18 years old</strong> to create an account, buy, or sell on Slab Society.
          </p>
          <p className="mt-3 text-zinc-300">
            This is required for legal compliance with payment processing and contract regulations.
          </p>
        </div>
      ),
      icon: User
    },
    {
      question: "What if I receive a damaged or wrong card?",
      answer: (
        <div>
          <p className="mb-3">
            If there's an issue with your order:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Message the seller directly through the platform</li>
            <li>If unresolved, contact us at support@slabsociety.com</li>
            <li>We'll help mediate and can assist with refund requests through Stripe</li>
          </ol>
          <p className="mt-3 text-zinc-500 text-xs">
            Always check seller ratings before purchasing and inspect cards immediately upon receipt.
          </p>
        </div>
      ),
      icon: HelpCircle
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="qa-page">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading font-bold text-4xl uppercase text-white mb-4">Questions & Answers</h1>
          <p className="text-zinc-400">Everything you need to know about Slab Society</p>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem 
              key={index} 
              question={faq.question} 
              answer={faq.answer}
              icon={faq.icon}
            />
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-zinc-500">
            Still have questions? Email us at{' '}
            <a href="mailto:support@slabsociety.com" className="text-blue-400 hover:underline">
              support@slabsociety.com
            </a>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default QAPage;
